export interface CountryConfig {
  id: string;
  slug: string;
  code: string;
  name: string;
  defaultLanguage: "fr" | "en";
  currency: string;
  currencySymbol: string;
  whatsappNumber: string;
  customerCarePhone: string;
  timezone: string;
  flag: string;
  preposition: "au" | "en";
  /** Expected number of digits AFTER the international dial code (e.g. CI +225 → 10). */
  expectedLocalPhoneLength?: number;
}

/** Expected local phone-number length (digits after the dial code) per country slug. */
export const PHONE_LOCAL_LENGTHS: Record<string, number> = {
  benin: 10,
  cameroun: 9,
  "burkina-faso": 8,
  "cote-divoire": 10,
  mali: 8,
  senegal: 9,
  togo: 8,
  uganda: 9,
  tanzanie: 9,
  maroc: 9,
};

/**
 * Pays où le "0" (ou "01") de tête fait partie du numéro international.
 * Ex. Côte d'Ivoire : 01 02 03 04 05 → +225 01 02 03 04 05 (13 chiffres).
 * Ailleurs (Uganda, Maroc, Tanzanie…) le 0 national est retiré en E.164.
 * Clé = indicatif sans "+".
 */
export const TRUNK_ZERO_DIALS = ["225", "229"];

/** True si le 0 de tête doit être conservé pour cet indicatif. */
export function keepsTrunkZero(dialDigits: string): boolean {
  return TRUNK_ZERO_DIALS.includes(dialDigits.replace(/\D/g, ""));
}

export const DEFAULT_COUNTRY_SLUG = "cote-divoire";


// Pays masqués du site public (visibles uniquement dans l'admin)
export const HIDDEN_COUNTRY_SLUGS = ["sandbox", "tanzanie"];

export function isHiddenCountrySlug(slug: string | null | undefined): boolean {
  return !!slug && HIDDEN_COUNTRY_SLUGS.includes(slug);
}

/**
 * Fallback statique des pays connus.
 * Sert d'initialData à react-query pour que le 1er render n'attende pas la DB
 * → débloque le LCP. Les valeurs sont synchronisées avec la table `countries`
 * et écrasées dès que la requête réseau résout.
 *
 * À mettre à jour quand un nouveau pays est ajouté en DB.
 */
export const STATIC_COUNTRIES: CountryConfig[] = [
  { id: "0cd2fc76-7d3a-489e-b274-fa9d1d20156a", slug: "benin",        code: "BJ",  name: "Bénin",                            defaultLanguage: "fr", currency: "XOF", currencySymbol: "FCFA", whatsappNumber: "+2290191510606", customerCarePhone: "+2290191510606", timezone: "Africa/Porto-Novo",    flag: "🇧🇯", preposition: "au", expectedLocalPhoneLength: 10 },
  { id: "8dadac5b-0052-4833-822d-0700401901df", slug: "burkina-faso", code: "BF",  name: "Burkina Faso",                     defaultLanguage: "fr", currency: "XOF", currencySymbol: "FCFA", whatsappNumber: "+22662642469",   customerCarePhone: "+22662642469",   timezone: "Africa/Ouagadougou",   flag: "🇧🇫", preposition: "au", expectedLocalPhoneLength: 8 },
  { id: "b4e8600d-7151-4371-8292-ac2011d8d7fc", slug: "cameroun",     code: "CM",  name: "Cameroun",                         defaultLanguage: "fr", currency: "XAF", currencySymbol: "FCFA", whatsappNumber: "+237659008545",  customerCarePhone: "+237659008545",  timezone: "Africa/Douala",        flag: "🇨🇲", preposition: "au", expectedLocalPhoneLength: 9 },
  { id: "4a56081d-3293-4e41-aec3-96a1d2a3c2f4", slug: "cote-divoire", code: "CI",  name: "Côte d'Ivoire",                    defaultLanguage: "fr", currency: "XOF", currencySymbol: "FCFA", whatsappNumber: "+2250703818278", customerCarePhone: "+2250703818278", timezone: "Africa/Abidjan",       flag: "🇨🇮", preposition: "en", expectedLocalPhoneLength: 10 },
  { id: "8fca87e9-6bba-4386-84ff-b5cd07f88239", slug: "mali",         code: "ML",  name: "Mali",                             defaultLanguage: "fr", currency: "XOF", currencySymbol: "FCFA", whatsappNumber: "+22373599726",   customerCarePhone: "+22373599726",   timezone: "Africa/Bamako",        flag: "🇲🇱", preposition: "au", expectedLocalPhoneLength: 8 },
  { id: "3cd1315b-b092-4604-b735-fa309d71fd53", slug: "maroc",        code: "MA",  name: "Maroc",                            defaultLanguage: "fr", currency: "MAD", currencySymbol: "MAD",  whatsappNumber: "+212669903206",  customerCarePhone: "+212669903206",  timezone: "Africa/Casablanca",    flag: "🇲🇦", preposition: "au", expectedLocalPhoneLength: 9 },
  { id: "73acf799-3a61-4630-b854-96a01cf04831", slug: "rdc",          code: "RDC", name: "République démocratique du Congo", defaultLanguage: "fr", currency: "USD", currencySymbol: "USD",  whatsappNumber: "+243",           customerCarePhone: "+243",           timezone: "Africa/Kinshasa",      flag: "🇨🇩", preposition: "en" },
  { id: "b092ffe7-1035-4d92-ae22-ca5354370d43", slug: "senegal",      code: "SN",  name: "Sénégal",                          defaultLanguage: "fr", currency: "XOF", currencySymbol: "FCFA", whatsappNumber: "+221784440909",  customerCarePhone: "+221784440909",  timezone: "Africa/Dakar",         flag: "🇸🇳", preposition: "au", expectedLocalPhoneLength: 9 },
  
  { id: "ad400175-cfc2-47da-a68a-a92d94482355", slug: "togo",         code: "TG",  name: "Togo",                             defaultLanguage: "fr", currency: "XOF", currencySymbol: "FCFA", whatsappNumber: "+22891993636",   customerCarePhone: "+22891993636",   timezone: "Africa/Lome",          flag: "🇹🇬", preposition: "au", expectedLocalPhoneLength: 8 },
  { id: "122f50c6-a396-48b9-9a3e-c54a9530b774", slug: "uganda",       code: "UG",  name: "Uganda",                           defaultLanguage: "en", currency: "UGX", currencySymbol: "UGX",  whatsappNumber: "+256709744775",  customerCarePhone: "+256709744775",  timezone: "Africa/Kampala",       flag: "🇺🇬", preposition: "au", expectedLocalPhoneLength: 9 },
];
